import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { FormInputText, FormDropdown } from '../../components/form';
import { RuleGroup } from '../waterfall-editor/RuleGroup';
import { SettingDataEditor } from './SettingDataEditor';
import { FeeRuleViewModel, FeeRuleType, SettingDataViewModel, FeeUpdateType } from '../../models/fees';
import { CustomFieldViewModel, CustomFieldDataType } from '../../models/configuration';
import { SimpleExpression, FieldMetadata, OperatorInfo, MetadataContextType, LogicalOperator, SpecialFieldType, DataType } from '../../models/expression';
import * as expressionService from '../../services/expressionService';
import * as configurationService from '../../services/configurationService';
import { mergeCustomFieldsWithMetadata } from '../../utils/custom-field-helper';

interface FeeRuleEditorDialogProps {
  rule: FeeRuleViewModel | null;
  visible: boolean;
  onClose: () => void;
  onSave: (rule: FeeRuleViewModel) => Promise<void> | void;
  ruleType: FeeRuleType | null;
}

interface FeeRuleFormData {
  description: string | null;
  ruleType: FeeRuleType | null;
  settingData: SettingDataViewModel[] | null;
}

export const FeeRuleEditorDialog: FC<FeeRuleEditorDialogProps> = ({ rule, visible, onClose, onSave, ruleType }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldMetadata, setFieldMetadata] = useState<FieldMetadata[]>([]);
  const [settingDataFieldMetadata, setSettingDataFieldMetadata] = useState<FieldMetadata[]>([]);
  const [operators, setOperators] = useState<OperatorInfo[]>([]);
  const [expression, setExpression] = useState<SimpleExpression | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dateFieldOptions, setDateFieldOptions] = useState<{ label: string; value: string }[]>([]);
  const initialExpressionRef = useRef<string>('');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FeeRuleFormData>({
    defaultValues: {
      description: rule?.description || null,
      ruleType: rule?.ruleType || ruleType || FeeRuleType.Initial,
      settingData: rule?.settingData || (
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.Initial || 
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.Final ||
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.RequestValue
          ? [{ fieldId: null, value: null, updateType: FeeUpdateType.SetValue }]
          : null
      ),
    },
  });

  const selectedRuleType = watch('ruleType');
  const formDescription = watch('description');
  const formSettingData = watch('settingData');

  useEffect(() => {
    if (visible) {
      const fetchMetadata = async () => {
        setIsLoading(true);
        try {
          const currentRuleType = selectedRuleType || ruleType || FeeRuleType.Initial;
          let contextType: MetadataContextType;
          let targetContextType: MetadataContextType | null = null;
          
          switch (currentRuleType) {
            case FeeRuleType.Initial:
              contextType = MetadataContextType.FeeInitial;
              targetContextType = MetadataContextType.FeeInitialTarget;
              break;
            case FeeRuleType.RequestValue:
              contextType = MetadataContextType.FeeRequestValue;
              targetContextType = MetadataContextType.FeeRequestValueTarget;
              break;
            case FeeRuleType.Final:
              contextType = MetadataContextType.FeeFinal;
              targetContextType = MetadataContextType.FeeFinalTarget;
              break;
            case FeeRuleType.IncludeFee:
              contextType = MetadataContextType.FeeIncludeFee;
              targetContextType = null;
              break;
            default:
              contextType = MetadataContextType.FeeInitial;
              targetContextType = MetadataContextType.FeeInitialTarget;
          }

          const promises: [
            Promise<FieldMetadata[]>,
            Promise<OperatorInfo[]>,
            Promise<any[]>,
            Promise<FieldMetadata[]> | Promise<never[]>
          ] = [
            expressionService.getFieldMetadata(contextType),
            expressionService.getOperators(),
            configurationService.getAllCustomFields(),
            targetContextType ? expressionService.getFieldMetadata(targetContextType) : Promise.resolve([]),
          ];

          const [fields, allOperators, customFields, targetFields] = await Promise.all(promises);

          const mergedFields = mergeCustomFieldsWithMetadata(fields, customFields);

          setFieldMetadata(mergedFields);
          setSettingDataFieldMetadata(targetFields);

          // Build date field options from request fields (Date/DateTime) and custom date fields
          const requestDateFields = fields
            .filter((f) => f.dataType === DataType.Date || f.dataType === DataType.DateTime)
            .map((f) => ({ label: f.fieldName || '', value: f.fieldName || '' }));
          const customDateFields = (customFields as CustomFieldViewModel[])
            .filter((cf) => cf.dataType === CustomFieldDataType.Date || cf.dataType === CustomFieldDataType.DateTime)
            .map((cf) => ({ label: cf.name || '', value: cf.name || '' }));
          setDateFieldOptions([...requestDateFields, ...customDateFields]);

          // TODO: "Not", "And", "Or" are logical operators - backend strips binary expressions using them. Use Equal/NotEqual/etc for comparisons.
          const filteredOperators = allOperators.filter(
            (op) => op.isLogical && !['Not', 'And', 'Or'].includes(op.operator)
          );
          setOperators(filteredOperators);
        } catch (error) {
          console.error('Failed to fetch field metadata:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchMetadata();
    }
  }, [visible, selectedRuleType, ruleType]);

  useEffect(() => {
    if (visible && rule) {
      const initialExpression = rule.expression || {
        expressions: [],
        logicalOperator: LogicalOperator.And,
        isNot: false,
      };
      setExpression(initialExpression);
      initialExpressionRef.current = JSON.stringify(initialExpression);
      reset({
        description: rule.description || null,
        ruleType: rule.ruleType || ruleType || FeeRuleType.Initial,
        settingData: deserializeSettingData(rule.settingData || null, settingDataFieldMetadata),
      });
    } else if (visible && !rule) {
      const initialExpression = {
        expressions: [] as any[],
        logicalOperator: LogicalOperator.And,
        isNot: false,
      };
      setExpression(initialExpression);
      initialExpressionRef.current = JSON.stringify(initialExpression);
      const initialRuleType = ruleType || FeeRuleType.Initial;
      reset({
        description: null,
        ruleType: initialRuleType,
        settingData: (initialRuleType === FeeRuleType.Initial || initialRuleType === FeeRuleType.Final || initialRuleType === FeeRuleType.RequestValue)
          ? [{ fieldId: null, value: null, updateType: FeeUpdateType.SetValue }]
          : null,
      });
    }
  }, [visible, rule, ruleType, reset]);

  useEffect(() => {
    if (visible && settingDataFieldMetadata.length > 0 && formSettingData) {
      const deserialized = deserializeSettingData(formSettingData, settingDataFieldMetadata);
      if (deserialized && JSON.stringify(deserialized) !== JSON.stringify(formSettingData)) {
        setValue('settingData', deserialized);
      }
    }
  }, [settingDataFieldMetadata]);

  useEffect(() => {
    if (selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) {
        if (!formSettingData || formSettingData.length === 0) {
          setValue('settingData', [{ fieldId: null, value: null, updateType: FeeUpdateType.SetValue }]);
        }
    }
  }, [selectedRuleType, formSettingData, setValue]);

  const ruleTypeOptions = useMemo(() => {
    return Object.values(FeeRuleType).map((type) => ({
      label: type,
      value: type,
    }));
  }, []);

  const isSettingValuePresent = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    return value != null && String(value).trim() !== '';
  };

  const isNullableTextField = (fieldId: string | null): boolean => {
    if (!fieldId) return false;
    const field = settingDataFieldMetadata.find((f) => f.fieldName === fieldId);
    if (!field) return false;
    if (field.specialField === SpecialFieldType.ServiceProviderSelection) return true;
    return field.dataType === DataType.Text && !field.isList;
  };

  const serializeSettingValue = (value: any): string => {
    if (Array.isArray(value)) return value.join(',');
    return String(value);
  };

  const deserializeSettingData = (settings: SettingDataViewModel[] | null, fields: FieldMetadata[]): SettingDataViewModel[] | null => {
    if (!settings || fields.length === 0) return settings;
    return settings.map((setting) => {
      const isSetValue = !setting.updateType || setting.updateType === FeeUpdateType.SetValue;
      if (setting.value == null && setting.fieldId && isSetValue && !setting.isDateBased && isNullableTextField(setting.fieldId)) {
        return { ...setting, setToEmpty: true, value: null };
      }
      return setting;
    });
  };

  const initialDescription = useMemo(
    () => rule?.description || null,
    [rule]
  );
  const initialRuleType = useMemo(
    () => rule?.ruleType || ruleType || FeeRuleType.Initial,
    [rule, ruleType]
  );
  const initialSettingDataJson = useMemo(
    () => JSON.stringify(deserializeSettingData(rule?.settingData || null, settingDataFieldMetadata)),
    // deserializeSettingData uses isNullableTextField, which closes over
    // settingDataFieldMetadata; recompute when the metadata loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rule, settingDataFieldMetadata]
  );

  const hasChanges = useMemo(() => {
    if (!rule) return true;
    const expressionChanged = JSON.stringify(expression) !== initialExpressionRef.current;
    const descriptionChanged = (formDescription || null) !== initialDescription;
    const ruleTypeChanged = (selectedRuleType || null) !== initialRuleType;
    const settingDataChanged = JSON.stringify(formSettingData ?? null) !== initialSettingDataJson;
    return expressionChanged || descriptionChanged || ruleTypeChanged || settingDataChanged;
  }, [rule, expression, formDescription, selectedRuleType, formSettingData, initialDescription, initialRuleType, initialSettingDataJson]);

  const isFormValid = useMemo(() => {
    const hasExpression = expression && 
                         expression.expressions && 
                         expression.expressions.length > 0 &&
                         expression.expressions.some(item => item.expression || item.expressionGroup);

    if (selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) {
      if (!formSettingData || formSettingData.length === 0) {
        return false;
      }

      const isSettingComplete = (setting: SettingDataViewModel) =>
        setting.fieldId && (
          isSettingValuePresent(setting.value) ||
          (setting.isDateBased && setting.dateFieldPath && setting.dateValues && setting.dateValues.length > 0) ||
          setting.setToEmpty === true
        );

      const hasAtLeastOneComplete = formSettingData.some(isSettingComplete);
      
      return hasExpression && hasAtLeastOneComplete;
    }

    return hasExpression;
  }, [selectedRuleType, formSettingData, expression, settingDataFieldMetadata]);

  const getDefaultUpdateType = (fieldId: string | null): FeeUpdateType => {
    const field = settingDataFieldMetadata.find((f) => f.fieldName === fieldId);
    if (field?.isList || field?.specialField === SpecialFieldType.ServiceProviderSelection) {
      return FeeUpdateType.AddValue;
    }
    return FeeUpdateType.SetValue;
  };

  const onSubmit = async (data: FeeRuleFormData) => {
    const filteredSettingData = data.settingData
      ?.filter((setting) => setting.fieldId && (
        isSettingValuePresent(setting.value) ||
        (setting.isDateBased && setting.dateFieldPath && setting.dateValues && setting.dateValues.length > 0) ||
        setting.setToEmpty === true
      ))
      .map((setting) => ({
        fieldId: setting.fieldId,
        value: setting.setToEmpty ? null : (setting.value != null ? serializeSettingValue(setting.value) : null),
        updateType: setting.updateType ?? getDefaultUpdateType(setting.fieldId),
        isDateBased: setting.isDateBased || false,
        dateFieldPath: setting.isDateBased ? setting.dateFieldPath : null,
        dateValues: setting.isDateBased ? (setting.dateValues || []) : [],
      })) || null;

    const updatedRule: FeeRuleViewModel = {
      ruleId: rule?.ruleId || '',
      description: data.description,
      expression: expression,
      ruleType: data.ruleType,
      settingData: filteredSettingData && filteredSettingData.length > 0 ? filteredSettingData : null,
    };

    setIsSaving(true);
    try {
      await onSave(updatedRule);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" className="p-button-secondary" onClick={onClose} disabled={isSaving} />
      <Button 
        label="Save" 
        className="p-button-primary" 
        onClick={handleSubmit(onSubmit)} 
        disabled={!isFormValid || !hasChanges || isSaving}
        loading={isSaving}
      />
    </div>
  );

  return (
    <Dialog
      header={rule ? 'Edit Rule' : 'Add Rule'}
      visible={visible}
      onHide={onClose}
      footer={footer}
      style={{ width: '90vw', maxWidth: '1200px' }}
      className="fee-rule-editor-dialog"
    >
      {isLoading ? (
        <div className="flex flex-column align-items-center justify-content-center gap-2" style={{ minHeight: '200px' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
          <span>Loading metadata...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-column gap-4">
            <div style={{ width: '375px' }}>
              <FormInputText
                control={control}
                errors={errors}
                field="description"
                label="Description"
                placeholder="Enter rule description"
                className="flex flex-column"
                inputClassName="border-1 border-solid border-gray-300 w-full"
              />
            </div>

            <div style={{ width: '375px' }}>
              <FormDropdown
                control={control}
                errors={errors}
                field="ruleType"
                label="Rule Type"
                options={ruleTypeOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Select rule type"
                className="flex flex-column"
                inputClassName="border-1 border-solid border-gray-300 w-full"
              />
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-medium">
                Expression<span className="text-red-500">*</span>
              </label>
              {expression && (
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                  <TabPanel header="Editor">
                    <RuleGroup
                      group={expression}
                      level={0}
                      onUpdate={(updatedExpression) => setExpression(updatedExpression)}
                      fieldMetadata={fieldMetadata}
                      operators={operators}
                    />
                  </TabPanel>
                  <TabPanel header="JSON">
                    <pre className="bg-gray-100 p-3 border-round overflow-auto" style={{ maxHeight: '400px' }}>
                      {JSON.stringify(expression, null, 2)}
                    </pre>
                  </TabPanel>
                </TabView>
              )}
            </div>

            {(selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) && (
              <SettingDataEditor 
                control={control} 
                errors={errors} 
                setValue={setValue} 
                fieldMetadata={settingDataFieldMetadata}
                dateFieldOptions={dateFieldOptions}
                ruleType={selectedRuleType}
              />
            )}
          </div>
        </form>
      )}
    </Dialog>
  );
};

